import localforage from 'localforage';
import logger from '~/services/logger';
import { isClientStorageAvailable } from './local-storage.client';

export const IS_LOGGED_IN_KEY: string = 'isLoggedIn';
export const AUTH_STATE_CHANGED_EVENT = 'zapp-auth:state-changed';
const IS_LOGGED_IN_REVALIDATION_INTERVAL_SECONDS: number = 60 * 60 * 24 * 5; // 5 days

function getIsLoggedInRevalidationIntervalSeconds(): number {
  if (typeof window === 'undefined') {
    return IS_LOGGED_IN_REVALIDATION_INTERVAL_SECONDS;
  }

  const configuredInterval = window.isLoggedInRevalidationIntervalSeconds;

  if (
    typeof configuredInterval !== 'number' ||
    !Number.isFinite(configuredInterval) ||
    configuredInterval <= 0
  ) {
    return IS_LOGGED_IN_REVALIDATION_INTERVAL_SECONDS;
  }

  return configuredInterval;
}

export function isLoggedInClientState(): {
  get: () => Promise<boolean | null>;
  set: (value: boolean) => Promise<void>;
  setRevalidate: () => Promise<void>;
  isStale: () => Promise<boolean>;
  remove: () => Promise<void>;
} {
  if (!isClientStorageAvailable()) {
    return {
      get: async () => null,
      set: async () => {},
      setRevalidate: async () => {},
      isStale: async () => true,
      remove: async () => {},
    };
  }

  const versionedIsLoggedInKey: string = getVersionedIsLoggedInKey();
  const isLoggedInRevalidateKey: string = `${versionedIsLoggedInKey}-revalidate`;

  const get = async (): Promise<boolean | null> => {
    try {
      const isLoggedIn: boolean | null = await localforage.getItem(
        versionedIsLoggedInKey
      );
      if (isLoggedIn === null) return null;
      return !!isLoggedIn;
    } catch (error: any) {
      logger.info(`isLoggedInClientState GET: ${error.message}`);
      return null;
    }
  };

  const set = async (value: boolean): Promise<void> => {
    const previousValue: boolean | null = await get();

    try {
      await localforage.setItem(versionedIsLoggedInKey, value);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(versionedIsLoggedInKey, `${value}`);
      }

      if (previousValue !== null && previousValue !== value) {
        dispatchAuthStateChangedEvent(value);
      }
    } catch (error: any) {
      logger.info(`isLoggedInClientState SET: ${error.message}`);
    }
  };

  const setRevalidate = async (): Promise<void> => {
    try {
      await localforage.setItem(isLoggedInRevalidateKey, Date.now());
    } catch (error: any) {
      logger.info(`isLoggedInClientState SET REVALIDATE: ${error.message}`);
    }
  };

  const isStale = async (): Promise<boolean> => {
    try {
      const isLoggedInRevalidate: number | null = await localforage.getItem(
        isLoggedInRevalidateKey
      );

      if (isLoggedInRevalidate === null) return true;

      const revalidationIntervalSeconds: number =
        getIsLoggedInRevalidationIntervalSeconds();

      return (
        Date.now() - isLoggedInRevalidate > revalidationIntervalSeconds * 1000
      );
    } catch (error: any) {
      logger.info(`isLoggedInClientState IS STALE: ${error.message}`);
      return true;
    }
  };

  const remove = async (): Promise<void> => {
    try {
      await localforage.removeItem(versionedIsLoggedInKey);
      await localforage.removeItem(isLoggedInRevalidateKey);
    } catch (error: any) {
      logger.info(`isLoggedInClientState REMOVE: ${error.message}`);
    }
  };

  return {
    get,
    set,
    setRevalidate,
    isStale,
    remove,
  };
}

export function getVersionedIsLoggedInKey(): string {
  const APP_VERSION_UUID: string = window.versionInfo?.APP_VERSION_UUID;
  return `${IS_LOGGED_IN_KEY}-${APP_VERSION_UUID}`;
}

export const REVALIDATE_LOGIN_STATE_QUERY_PARAM: string =
  'revalidateLoginState';

export function getIsRevalidateLoginStateFromRedirect(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const fromRedirect = urlParams.get(REVALIDATE_LOGIN_STATE_QUERY_PARAM);
  return !!fromRedirect;
}

/**
 * Dispatches an auth state change event on `window`.
 *
 * @param isLoggedIn - Updated login state.
 * @returns Nothing.
 */
export function dispatchAuthStateChangedEvent(isLoggedIn: boolean): void {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(AUTH_STATE_CHANGED_EVENT, { detail: { isLoggedIn } })
  );
}
